/**
 * Breadcrumbs — a bounded in-memory trail of recent UI activity.
 *
 * When a crash report is sent (see `api/clientErrors.ts`), the last N
 * breadcrumbs are attached so a backend reviewer can see what the user
 * was doing in the seconds before the crash: which route they were on,
 * which API calls fired (with trace IDs that cross-link to server logs).
 *
 * The buffer is a plain module-level array. It lives in the JS heap, so
 * it survives a React render crash — `componentDidCatch` and the
 * `window.onerror` handler run in the same JS context and can still
 * read it. A full page navigation/reload is the only thing that clears
 * it, which is correct: a fresh page is a fresh trail.
 */

export type BreadcrumbKind = 'route' | 'api' | 'other';

export interface Breadcrumb {
  kind: BreadcrumbKind;
  /** Human-readable summary, e.g. "GET /api/packets/42/ → 500". */
  message: string;
  /** ISO-8601 timestamp from the client clock. */
  at: string;
  /** Small structured payload, e.g. { status, traceId }. */
  data?: Record<string, unknown>;
}

/** Keep the buffer small — it ships inside every crash report body. */
const MAX_BREADCRUMBS = 40;

const buffer: Breadcrumb[] = [];

/** Record one breadcrumb. Oldest entries drop once the cap is hit. */
export function addBreadcrumb(
  kind: BreadcrumbKind,
  message: string,
  data?: Record<string, unknown>,
): void {
  buffer.push({
    kind,
    message,
    at: new Date().toISOString(),
    ...(data ? { data } : {}),
  });
  if (buffer.length > MAX_BREADCRUMBS) {
    buffer.splice(0, buffer.length - MAX_BREADCRUMBS);
  }
}

/** A snapshot copy of the current trail, oldest first. */
export function getBreadcrumbs(): Breadcrumb[] {
  return buffer.slice();
}

/** Empty the buffer. Exposed for tests and post-report cleanup. */
export function clearBreadcrumbs(): void {
  buffer.length = 0;
}
